import { Amplify } from "aws-amplify";
import { FC, useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router";

import { Form } from "capitalrx-components-2.0/lib/forms";
import { Modal, useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";

import {
  PreRegisterUserRequest,
  PreRegisterUserUnprocessable,
  RegisterUserRequest,
  cognitoRegisterUser,
  preRegisterUser,
  registerUser,
} from "@/App/api/login";
import { ClientAwareLink } from "@/App/components/ClientAwareLink";
import { StorageKeys } from "@/App/constants/constants";
import { AuthenticationContext } from "@/App/context";
import { useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";

import { AuthTypeForm } from "./AuthTypeForm";
import { ConfirmRegistrationForm } from "./ConfirmRegistrationForm";
import { PreRegisterForm } from "./PreRegisterForm";

interface RegistrationForm {
  readonly firstName: string;
  readonly lastName: string;
  readonly dateOfBirth: string;
  readonly registrationMethod: "ssn" | "memberId";
  readonly registrationValue: string;
  readonly email: string;
  readonly password: string;
  readonly confirmPassword: string;
  readonly optIntoEmails: boolean;
  readonly createAccountMethod: "native" | "sso";
}

export const RegisterPage: FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t: translate } = useTranslation();
  const alert = useAlert();

  const [forceFirstNameReadonly, setForceFirstNameReadonly] = useState(false);
  const [forceLastNameReadonly, setForceLastNameReadonly] = useState(false);
  const [forceDateOfBirthReadonly, setForceDateOfBirthReadonly] = useState(false);
  const [forceMemberIdReadonly, setForceMemberIdReadonly] = useState(false);
  const [forceEmailReadonly, setForceEmailReadonly] = useState(false);
  const { setAuthState } = useContext(AuthenticationContext);

  const isSSOPreregister = useMemo(() => {
    return location.pathname === "/sso/register";
  }, [location.pathname]);

  const [preregisterUnprocessableData, setPreregisterUnprocessableData] = useState<PreRegisterUserUnprocessable>(undefined);

  const defaultFormValues: RegistrationForm = useMemo(() => {
    const authToken = localStorage.getItem("_auth");
    if (authToken) {
      let tokenData;
      try {
        const parts = authToken.split(".");
        tokenData = JSON.parse(atob(parts[1]));
      } catch (e) {
        tokenData = { jti: "", iat: 0, exp: 0, nbf: 0, token_use: "", username: "" };
      }

      const attributes = { ...JSON.parse(localStorage.getItem(StorageKeys.ATTRIBUTES)) };
      setForceFirstNameReadonly(!!attributes.first_name);
      setForceLastNameReadonly(!!attributes.last_name);
      setForceDateOfBirthReadonly(!!attributes.date_of_birth);
      setForceMemberIdReadonly(!!attributes.external_member_id);
      setForceEmailReadonly(!!tokenData.username);

      return {
        firstName: attributes.first_name || "",
        lastName: attributes.last_name || "",
        dateOfBirth: attributes.date_of_birth || null,
        registrationMethod: "memberId",
        registrationValue: attributes.external_member_id || "",
        email: tokenData.username || "",
        password: "",
        confirmPassword: "",
        optIntoEmails: true,
        createAccountMethod: "sso",
      };
    } else {
      return {
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        registrationMethod: "ssn",
        registrationValue: "",
        email: "",
        password: "",
        confirmPassword: "",
        optIntoEmails: true,
        createAccountMethod: !preregisterUnprocessableData || preregisterUnprocessableData?.allow_optional ? "native" : "sso",
      };
    }
  }, [preregisterUnprocessableData]);

  const form = useForm<RegistrationForm>({
    defaultValues: defaultFormValues,
  });

  const navigate = useNavigateClient();
  const [formId, setFormId] = useState<"preRegister" | "selectAuth" | "confirmRegister">("preRegister");
  const [spDisabledModalData, setSpDisabledModalData] = useState<{ message: string; samlRedirect: string; messageUrl: string; }>(undefined);
  const registrationMethod = form.watch("registrationMethod");

  const submitForm = async (formData: RegistrationForm) => {
    if (formId === "preRegister") {
      await runPreRegisterUser(formData);
    } else if (formId === "confirmRegister") {
      await confirmRegisterUser(formData);
    } else if (formId === "selectAuth") {
      selectUserAuthType(formData);
    }
  };

  const confirmRegisterUser = async (formData: RegistrationForm) => {
    let body: RegisterUserRequest = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      date_of_birth: formData.dateOfBirth,
      opt_in_to_emails: formData.optIntoEmails,
      email: formData.email,
    };
    if (registrationMethod === "memberId") {
      body = { ...body, external_member_id: formData.registrationValue };
    } else {
      const formattedSSN = formData.registrationValue.replaceAll("-", "").replaceAll(" ", "");
      body = { ...body, social_security_number: formattedSSN };
    }

    if (isSSOPreregister) {
      body = { ...body, authenticator: "sso" };
      await registerUser(body);
      logEvent(ANALYTIC_EVENTS.ACCOUNT.create);
      setAuthState("signedIn");
      navigate(searchParams.get("target_url") || "/");
    } else {
      // Create app user registration
      let registerUserResponse = null;
      try {
        registerUserResponse = await registerUser(body);
      } catch (error) {
          logEvent(ANALYTIC_EVENTS.ERRORS.registerMember, {
          context: "RegisterPage app account create",
          errorName: error?.name,
          errorMessage: error?.data?.message,
        });

        alert({
          message: error?.data?.message || translate("REGISTER.registerPage.failedToRegister"),
          type: "error",
          style: "toast",
        });
        return;
      }

      // Create Cognito user tied to user app registration
      try {
        // extra logging added to debug registration errors "Auth UserPool not configured"
        const amplifyConfig = Amplify.getConfig();
        logEvent(ANALYTIC_EVENTS.ACCOUNT.beforeCreate, { "context": "RegisterPage", "amplifyConfig": amplifyConfig });

        if (!registerUserResponse?.member?.id || !amplifyConfig) {
          logEvent(ANALYTIC_EVENTS.ERRORS.registerMember, {
            "context": "RegisterPage missing context",
            "amplifyConfig": amplifyConfig,
            "isMissingAppRegistration": !registerUserResponse?.member?.id,
          });
          return;
        }

        // returns { isSignUpComplete, nextStep, userId }
        const signUpOutput = await cognitoRegisterUser({
          email: formData.email,
          password: formData.password,
          member: { ...registerUserResponse.member },
          plan: { ...registerUserResponse.plan },
        });
        logEvent(ANALYTIC_EVENTS.ACCOUNT.create, { context: "RegisterPage", "isSignUpComplete": signUpOutput?.isSignUpComplete });
        navigate(`/confirm?email=${encodeURIComponent(formData.email.trim().toLowerCase())}`);
      } catch (error) {
        // Amplify sign-up error types: https://docs.amplify.aws/react/build-a-backend/auth/reference/#signUp-54347
        logEvent(ANALYTIC_EVENTS.ERRORS.registerMember, {
          "context": "RegisterPage",
          "errorName": error?.name,
          "errorMessage": error?.data?.message,
        });
        if (error?.name === "InvalidPasswordException") {
          alert({
            message: translate("REGISTER.registerPage.passwordCommonlyUsed"),
            style: "banner",
            type: "error",
            timeout: 10000,
          });
        } else {
          alert({
            message: error?.data?.message || translate("REGISTER.registerPage.failedToRegister"),
            type: "error",
            style: "toast",
          });
        }
      }
    }
  };

  const runPreRegisterUser = async (formData: RegistrationForm) => {
    try {
      let body: PreRegisterUserRequest = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        date_of_birth: formData.dateOfBirth,
      };
      if (isSSOPreregister) {
        body = { ...body, post_auth: "true" };
      }
      if (registrationMethod === "memberId") {
        body = { ...body, external_member_id: formData.registrationValue.toUpperCase() };
      } else {
        const formattedSSN = formData.registrationValue.replaceAll("-", "").replaceAll(" ", "");
        body = { ...body, social_security_number: formattedSSN };
      }
      const response: PreRegisterUserUnprocessable = await preRegisterUser(body);

      if (response.status === "success") {
        logEvent(ANALYTIC_EVENTS.ACCOUNT.validate);
        setFormId("confirmRegister");
      } else if (response.status === "conditional") {
        if (response.allow_optional) {
          setPreregisterUnprocessableData(response);
          setFormId("selectAuth");
        } else if (response.sp_disabled === false) {
          setSpDisabledModalData({
            message: translate("REGISTER.registerPage.accountAssociatedWarningLink"),
            samlRedirect: response.saml_redirect,
            messageUrl: translate("REGISTER.registerPage.redirectMessage"),
          });
          setTimeout(() => {
            window.location.href = response.saml_redirect;
          }, 3000);
        } else {
          setSpDisabledModalData({
            message: response.message,
            samlRedirect: response.message_url,
            messageUrl: response.message_url,
          });
        }
      } else {
        const message = translate("REGISTER.registerPage.preRegistrationStatus");
        alert({
          message: message,
          type: "error",
          style: "toast",
        });
      }
    } catch (e) {
      logEvent(ANALYTIC_EVENTS.ERRORS.validateMember);
      const message = e?.data?.message || translate("REGISTER.registerPage.unableValidateInformationMessage");
      alert({
        message: message,
        type: "error",
        style: "toast",
      });
    }
  };

  const selectUserAuthType = async (formData: RegistrationForm) => {
    const getPreregisterMessage = () => {
      if (preregisterUnprocessableData.message) {
        return preregisterUnprocessableData.message;
      } else {
        return translate("REGISTER.registerPage.accountAssociatedWarningLink");
      }
    };

    if (formData.createAccountMethod === "native") {
      setFormId("confirmRegister");
    } else {
      if (preregisterUnprocessableData.sp_disabled) {
        setSpDisabledModalData({
          message: getPreregisterMessage(),
          samlRedirect: preregisterUnprocessableData.message_url,
          messageUrl: preregisterUnprocessableData.message_url,
        });
      } else {
        window.location.href = preregisterUnprocessableData.saml_redirect;
      }
    }
  };

  const modalClosed = () => {
    navigate("/login");
  };

  return (
    <Form form={form} onSubmit={submitForm} testId="register">
      <Modal
        testId="sso-warning"
        onClose={modalClosed}
        visible={spDisabledModalData !== undefined}
        title={translate("REGISTER.registerPage.SSOLogin")}
      >
        <Paragraph testId="sso-warning-message">
          {spDisabledModalData?.message || translate("REGISTER.registerPage.accountAssociatedWarning")}
        </Paragraph>
        <a data-testid="a-sso-warning-redirect" href={spDisabledModalData?.samlRedirect}>
          {spDisabledModalData?.messageUrl}
        </a>
      </Modal>
      <Stack>
        {formId === "preRegister" && (
          <PreRegisterForm
            forceMemberIdReadonly={forceMemberIdReadonly}
            forceLastNameReadonly={forceLastNameReadonly}
            forceFirstNameReadonly={forceFirstNameReadonly}
            forceDateOfBirthReadonly={forceDateOfBirthReadonly}
          />
        )}
        {formId === "confirmRegister" && (
          <ConfirmRegistrationForm
            forceEmailReadonly={forceEmailReadonly}
            stepNumber={preregisterUnprocessableData === undefined ? "2" : "3"}
          />
        )}
        {formId === "selectAuth" && <AuthTypeForm identityProvider={preregisterUnprocessableData?.identity_provider} />}
        <Shelf>
          <Shelf.Spacer />
          <ClientAwareLink testId="to-login" to="/login">
            {translate("REGISTER.registerPage.alreadyHaveAnAccount")}
          </ClientAwareLink>
          <Shelf.Spacer />
        </Shelf>
      </Stack>
    </Form>
  );
};
